import { useEffect, useState } from 'react';
import { NOMS_DES_COULEURS, type FicheDeBulle, type HabitDeProjet, type ModeDeLaCarte } from '@nextilt/commun';
import type { ChangementDHabit, ModeDUneFeuille } from '../partage/api/types';
import { teinteOuAccent } from '../apparence';
import { ChoixEntoures } from '../fiche';
import { ApercuDeForme } from '../partage/ApercuDeForme';
import { EchantillonDePapier } from '../partage/EchantillonDePapier';
import { IconeCintre } from '../partage/icones';
import { useChoisirLeMode, useHabiller } from '../partage/useArbre';
import { useMenuOuvert } from '../partage/useMenuOuvert';
import { rienNEcouteEchap } from '../partage/useEchap';
import { enTrainDEcrire } from './useTouchesDeLaCarte';
import {
  COULEUR_DU_PROJET,
  EXPLICATION_DE_L_HABIT,
  FOND_DE_LA_CARTE,
  FORME_DES_BULLES_DU_PROJET,
  HABIT_DU_PROJET,
  HABIT_PAS_ECRIT,
  COMME_LA_MERE,
  EXPLICATION_DU_MODE,
  MODE,
  MODE_DE_LA_BULLE,
  MODE_DU_PROJET,
  PHRASE_COMME_LA_MERE,
  MODE_PAS_ECRIT,
  MOTS_DES_MODES,
  ONGLET_HABIT,
  ONGLET_MODE,
  DANS_LES_MODES,
  MOTS_DES_ANGLES_D_OUVERTURE,
  MOTS_DES_FONDS_DE_PROJET,
  MOTS_DES_FORMES_DE_PROJET,
  MOTS_DES_PAPIERS_DE_FICHE,
  MOTS_DES_TRAITS_DE_PROJET,
  MOTS_DES_TYPES_DE_FICHE,
  PAPIER_DE_LA_FICHE,
  PAPIER_FIXE_PAR_LE_TYPE,
  S_OUVRE_EN,
  TRAIT_DES_BULLES,
  TYPE_DE_FICHE_EN_ENTIER,
} from '../partage/vocabulaire';
import { CHOIX_DE_L_HABIT } from './habit';
import {
  gesteEnMots,
  MODES,
  modesQuiGardentTout,
  TOUCHES_DE_PARTOUT,
  TOUCHES_DES_MODES,
  touchesDuMode,
  type Raccourci,
} from './modes';
import './HabitDuProjet.css';

/** Les deux onglets à plat du panneau (chantier modes-de-la-carte, modification 01). */
type Onglet = 'habit' | 'mode';

/**
 * Le bouton « Habit » d'un projet ouvert, et son petit panneau (chantier habit-du-projet, ticket
 * 02, décisions d'Igor du 2026-10-20) : à l'ordinateur, dans la tête du projet, à côté des angles
 * (et du ⚙ en Hiérarchie). Le panneau règle la couleur du projet (les cinq) et le fond de sa carte
 * (uni, lignes, carreaux), la forme de ses bulles (les huit, ticket 03) et le trait de leur contour
 * (les cinq, ticket 04), chacun avec son aperçu ; un choix s'applique aussitôt et s'enregistre
 * (`useHabiller`). Les aperçus des formes portent le trait du projet, ceux des traits sa forme.
 * Le type et le papier par défaut de ses fiches (chantier types-de-fiche, modification 06) : les
 * fiches de ses bulles qui n'ont rien choisi les prennent. L'angle sur lequel il s'ouvre (chantier
 * angle-a-l-ouverture, modification 02), en dernier.
 *
 * Le modèle est le ⚙ (`ParametresDeFeuille`) : il se replie sur un clic à côté et sur Échap
 * (`useMenuOuvert`).
 *
 * **« Habit » devient « Mode »** (chantier modes-de-la-carte, modification 01, décisions d'Igor du
 * 2026-10-21) : le bouton dit « Mode », le panneau a deux onglets à plat, soulignés comme les
 * Réglages de la fiche (`.intercalaires-onglets`) : Habit, tel qu'avant, et Mode, les quatre
 * préréglages des touches de la carte l'un sous l'autre, chacun son nom, sa phrase et ses touches
 * (`TOUCHES_DES_MODES`), celles de partout dites une fois. Le choisi est entouré au crayon
 * (`ChoixEntoures`). Analyse se choisit comme les autres (chantier theme-analyse, ticket 01) ; il ne
 * garde que trois touches de partout, et dit donc toutes les siennes : G, M, /, Échap, Ctrl ; les
 * touches de partout sont dites pour les modes qui les gardent toutes.
 *
 * **Le mode à tous les niveaux** (deuxième passe de 01 et 02, retour d'Igor du 2026-10-21) :
 * « Mode » est dans la tête de toute feuille ; le panneau règle le mode de la bulle ouverte, que
 * ses étapes prennent tant qu'elles n'en ont pas un. Dans une bulle, « Comme <la mère> » vient en
 * tête, et le panneau n'a que le Mode : l'Habit reste au projet.
 */
export function HabitDuProjet({
  bulleId,
  projet,
  mode,
  mere,
}: {
  bulleId: string;
  /** Le projet ouvert, son habit et ses fiches par défaut ; `null` dans une bulle (pas d'onglet Habit). */
  projet: { couleur: string | null; habit: HabitDeProjet; fiches: FicheDeBulle } | null;
  mode: ModeDUneFeuille;
  /** Le titre de la mère de la bulle ouverte ; `null` sur le projet. */
  mere: string | null;
}) {
  const panneau = useMenuOuvert();
  const habiller = useHabiller();
  const choisirLeMode = useChoisirLeMode();
  const choisir = (changement: ChangementDHabit) => habiller.mutate({ projetId: bulleId, changement });
  const [onglet, setOnglet] = useState<Onglet>('mode');
  const titre = projet ? MODE_DU_PROJET : MODE_DE_LA_BULLE;

  /*
   * Ctrl seul ouvre le panneau sur l'onglet Mode, et le referme (retour d'Igor du 2026-10-21 :
   * « il faut une touche pour afficher les mode rappidement genre ctrl ») : Ctrl appuyé puis
   * relâché sans autre touche ni clic entre deux, pour ne jamais partir sur Ctrl+C ou Ctrl+clic.
   * Il ne s'ouvre pas pendant qu'on écrit, ni quand une fiche ou un menu est ouvert.
   */
  useEffect(() => {
    let seul = false;
    const enBas = (e: KeyboardEvent) => {
      seul = e.key === 'Control' && !e.repeat && !e.shiftKey && !e.altKey && !e.metaKey;
    };
    const enHaut = (e: KeyboardEvent) => {
      if (e.key !== 'Control' || !seul) return;
      seul = false;
      if (panneau.ouvert) {
        panneau.setOuvert(false);
        return;
      }
      if (enTrainDEcrire(document.activeElement) || !rienNEcouteEchap()) return;
      setOnglet('mode');
      panneau.setOuvert(true);
    };
    const clic = () => {
      seul = false;
    };
    window.addEventListener('keydown', enBas);
    window.addEventListener('keyup', enHaut);
    window.addEventListener('pointerdown', clic);
    return () => {
      window.removeEventListener('keydown', enBas);
      window.removeEventListener('keyup', enHaut);
      window.removeEventListener('pointerdown', clic);
    };
  }, [panneau]);

  return (
    <div className="habit" ref={panneau.ref}>
      <button
        type="button"
        className="habit-bouton"
        aria-haspopup="dialog"
        aria-expanded={panneau.ouvert}
        title={titre}
        onClick={() => panneau.setOuvert(!panneau.ouvert)}
      >
        <IconeCintre className="habit-icone" />
        {MODE}
      </button>

      {panneau.ouvert && (
        <div className="habit-panneau" role="dialog" aria-label={titre}>
          {projet && (
            <div className="intercalaires-onglets habit-onglets" role="tablist" aria-label={titre}>
              {(
                [
                  ['mode', ONGLET_MODE],
                  ['habit', ONGLET_HABIT],
                ] as const
              ).map(([o, mot]) => (
                <button
                  key={o}
                  type="button"
                  role="tab"
                  aria-selected={o === onglet}
                  data-onglet={o}
                  className={o === onglet ? 'est-choisi' : ''}
                  onClick={() => setOnglet(o)}
                >
                  {mot}
                </button>
              ))}
            </div>
          )}

          {(onglet === 'mode' || !projet) && (
            <PageDuMode
              titre={titre}
              mode={mode}
              mere={mere}
              onChoisir={(m) => choisirLeMode.mutate({ id: bulleId, changement: { mode: m } })}
              pasEcrit={choisirLeMode.isError}
            />
          )}

          {onglet === 'habit' && projet && (
            <HabitDuPanneau {...projet} choisir={choisir} pasEcrit={habiller.isError} />
          )}
        </div>
      )}
    </div>
  );
}

/** L'onglet Habit, celui du projet seulement, tel qu'avant. */
function HabitDuPanneau({
  couleur,
  habit,
  fiches,
  choisir,
  pasEcrit,
}: {
  couleur: string | null;
  habit: HabitDeProjet;
  /** Le type et le papier par défaut des fiches du projet (`Feuille.fiches`, défauts compris). */
  fiches: FicheDeBulle;
  choisir: (changement: ChangementDHabit) => void;
  pasEcrit: boolean;
}) {
  return (
    <>
      <p className="habit-titre">{HABIT_DU_PROJET}</p>
      <p className="habit-explication">{EXPLICATION_DE_L_HABIT}</p>

      <div className="habit-rangee" role="radiogroup" aria-label={COULEUR_DU_PROJET} data-habit="couleur">
        <span className="habit-nom">{COULEUR_DU_PROJET}</span>
        <div className="habit-choix">
          {CHOIX_DE_L_HABIT.couleurs.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={couleur === c}
              aria-label={NOMS_DES_COULEURS[c]}
              title={NOMS_DES_COULEURS[c]}
              data-valeur={c}
              className="habit-couleur"
              style={{ ['--pastille' as string]: teinteOuAccent(c) }}
              onClick={() => {
                if (couleur !== c) choisir({ couleur: c });
              }}
            />
          ))}
        </div>
      </div>

      <div className="habit-rangee" role="radiogroup" aria-label={FOND_DE_LA_CARTE} data-habit="fond">
        <span className="habit-nom">{FOND_DE_LA_CARTE}</span>
        <div className="habit-choix">
          {CHOIX_DE_L_HABIT.fonds.map((f) => (
            <button
              key={f}
              type="button"
              role="radio"
              aria-checked={habit.fond === f}
              data-valeur={f}
              className="habit-fond"
              onClick={() => {
                if (habit.fond !== f) choisir({ fond: f });
              }}
            >
              <EchantillonDePapier papier={f === 'uni' ? 'blanche' : f} grand />
              {MOTS_DES_FONDS_DE_PROJET[f]}
            </button>
          ))}
        </div>
      </div>

      {/* La forme des bulles (ticket 03) : la Carte et la Hiérarchie du projet. */}
      <div className="habit-rangee" role="radiogroup" aria-label={FORME_DES_BULLES_DU_PROJET} data-habit="forme">
        <span className="habit-nom">{FORME_DES_BULLES_DU_PROJET}</span>
        <div className="habit-choix habit-choix--formes">
          {CHOIX_DE_L_HABIT.formes.map((f) => (
            <button
              key={f}
              type="button"
              role="radio"
              aria-checked={habit.forme === f}
              data-valeur={f}
              className="habit-forme"
              onClick={() => {
                if (habit.forme !== f) choisir({ forme: f });
              }}
            >
              <ApercuDeForme forme={f} trait={habit.trait} />
              {MOTS_DES_FORMES_DE_PROJET[f]}
            </button>
          ))}
        </div>
      </div>

      {/* Le trait du contour (ticket 04) : chacun en aperçu, dans la forme du projet. */}
      <div className="habit-rangee" role="radiogroup" aria-label={TRAIT_DES_BULLES} data-habit="trait">
        <span className="habit-nom">{TRAIT_DES_BULLES}</span>
        <div className="habit-choix habit-choix--traits">
          {CHOIX_DE_L_HABIT.traits.map((t) => (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={habit.trait === t}
              data-valeur={t}
              className="habit-forme"
              onClick={() => {
                if (habit.trait !== t) choisir({ trait: t });
              }}
            >
              <ApercuDeForme forme={habit.forme} trait={t} />
              {MOTS_DES_TRAITS_DE_PROJET[t]}
            </button>
          ))}
        </div>
      </div>

      {/* Le type et le papier par défaut des fiches du projet (types-de-fiche, 06). */}
      <div className="habit-rangee" role="radiogroup" aria-label={TYPE_DE_FICHE_EN_ENTIER} data-habit="type-de-fiche">
        <span className="habit-nom">{TYPE_DE_FICHE_EN_ENTIER}</span>
        <div className="habit-choix habit-choix--types">
          {CHOIX_DE_L_HABIT.typesDeFiche.map((t) => (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={fiches.type === t}
              data-valeur={t}
              className="habit-forme habit-type"
              onClick={() => {
                if (fiches.type !== t) choisir({ typeParDefaut: t });
              }}
            >
              {MOTS_DES_TYPES_DE_FICHE[t]}
            </button>
          ))}
        </div>
      </div>

      <div className="habit-rangee" role="radiogroup" aria-label={PAPIER_DE_LA_FICHE} data-habit="papier-de-fiche">
        <span className="habit-nom" title={PAPIER_FIXE_PAR_LE_TYPE}>
          {PAPIER_DE_LA_FICHE}
        </span>
        <div className="habit-choix habit-choix--papiers">
          {CHOIX_DE_L_HABIT.papiersDeFiche.map((p) => (
            <button
              key={p}
              type="button"
              role="radio"
              aria-checked={fiches.papier === p}
              data-valeur={p}
              className="habit-fond habit-papier"
              onClick={() => {
                if (fiches.papier !== p) choisir({ papierParDefaut: p });
              }}
            >
              <EchantillonDePapier papier={p} grand />
              {MOTS_DES_PAPIERS_DE_FICHE[p]}
            </button>
          ))}
        </div>
      </div>

      {/* L'angle sur lequel le projet s'ouvre (angle-a-l-ouverture, 02) : Carte, Liste, Hiérarchie. */}
      <div className="habit-rangee" role="radiogroup" aria-label={S_OUVRE_EN} data-habit="angle">
        <span className="habit-nom">{S_OUVRE_EN}</span>
        <div className="habit-choix habit-choix--angles">
          {CHOIX_DE_L_HABIT.angles.map((a) => (
            <button
              key={a}
              type="button"
              role="radio"
              aria-checked={habit.angle === a}
              data-valeur={a}
              className="habit-forme habit-type"
              onClick={() => {
                if (habit.angle !== a) choisir({ angle: a });
              }}
            >
              {MOTS_DES_ANGLES_D_OUVERTURE[a]}
            </button>
          ))}
        </div>
      </div>

      {pasEcrit && (
        <p className="habit-refus" role="status">
          {HABIT_PAS_ECRIT}
        </p>
      )}
    </>
  );
}

/** Une touche et ce qu'elle fait : « C · le contact, son numéro, et le lieu ». */
function LigneDeTouche({ raccourci }: { raccourci: Raccourci }) {
  return (
    <li className="mode-touche">
      <kbd>{raccourci.touche}</kbd>
      <span>{gesteEnMots(raccourci.geste)}</span>
    </li>
  );
}

/** « Comme la mère » : le choix d'une bulle qui n'a pas de mode à elle. */
const COMME = 'comme';

/** « Dans De base, Réflexion et Exécution » : les modes qui gardent toutes les touches de partout. */
const dansLesModes = DANS_LES_MODES(modesQuiGardentTout().map((m) => MOTS_DES_MODES[m].nom));

/**
 * L'onglet Mode : l'explication, dans une bulle « Comme <la mère> » (le mode qu'elle en prend),
 * puis les quatre modes l'un sous l'autre (le nom, entouré s'il est choisi ; la phrase ; les
 * touches qu'il ajoute), puis celles de partout, dites une fois. Un choix s'enregistre au clic,
 * comme l'habit. Un projet sans mode à lui est en De base, entouré.
 */
function PageDuMode({
  titre,
  mode,
  mere,
  onChoisir,
  pasEcrit,
}: {
  titre: string;
  mode: ModeDUneFeuille;
  mere: string | null;
  onChoisir: (mode: ModeDeLaCarte | null) => void;
  pasEcrit: boolean;
}) {
  const choisi: string = mere !== null ? (mode.sien ?? COMME) : (mode.sien ?? mode.deLaMere);
  return (
    <div className="mode-page" role="tabpanel" aria-label={ONGLET_MODE}>
      <p className="habit-titre">{titre}</p>
      <p className="habit-explication">{EXPLICATION_DU_MODE}</p>
      {mere !== null && (
        <section className="mode-un" data-mode={COMME} aria-label={COMME_LA_MERE(mere)}>
          <div className="mode-tete">
            <ChoixEntoures<string>
              nom={COMME_LA_MERE(mere)}
              choix={[{ cle: COMME, mot: COMME_LA_MERE(mere) }]}
              choisi={choisi}
              onChoisir={() => {
                if (mode.sien !== null) onChoisir(null);
              }}
            />
          </div>
          <p className="mode-phrase">{PHRASE_COMME_LA_MERE(MOTS_DES_MODES[mode.deLaMere].nom)}</p>
        </section>
      )}
      {MODES.map((m) => {
        // Un mode qui ne garde pas toutes celles de partout dit toutes les siennes (Analyse).
        const touches = TOUCHES_DES_MODES[m].dePartout === 'toutes' ? TOUCHES_DES_MODES[m].touches : touchesDuMode(m);
        const { nom, phrase } = MOTS_DES_MODES[m];
        return (
          <section key={m} className="mode-un" data-mode={m} aria-label={nom}>
            <div className="mode-tete">
              <ChoixEntoures<string>
                nom={nom}
                choix={[{ cle: m, mot: nom }]}
                choisi={choisi}
                onChoisir={() => {
                  if (m !== mode.sien) onChoisir(m);
                }}
              />
            </div>
            <p className="mode-phrase">{phrase}</p>
            {touches.length > 0 && (
              <ul className="mode-touches">
                {touches.map((r) => (
                  <LigneDeTouche key={r.cle} raccourci={r} />
                ))}
              </ul>
            )}
          </section>
        );
      })}
      <section className="mode-un mode-partout" aria-label={dansLesModes}>
        <p className="mode-partout-titre">{dansLesModes}</p>
        <ul className="mode-touches">
          {TOUCHES_DE_PARTOUT.map((r) => (
            <LigneDeTouche key={r.cle} raccourci={r} />
          ))}
        </ul>
      </section>
      {pasEcrit && (
        <p className="habit-refus" role="status">
          {MODE_PAS_ECRIT}
        </p>
      )}
    </div>
  );
}
